import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Instagram, Loader2, ArrowUpRight } from 'lucide-react';

export default function InstagramGallery() {
  const { data = { media: [] }, isLoading } = useQuery({
    queryKey: ['instagram-feed'],
    queryFn: async () => {
      const res = await base44.functions.invoke('instagramFeed', {});
      return res.data || { media: [] };
    },
    staleTime: 1000 * 60 * 10,
  });

  const media = data.media || [];
  if (!isLoading && media.length === 0) return null;

  return (
    <section className="py-24 px-6 lg:px-10 bg-[#0d0d0f] border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">FROM THE COMMUNITY</p>
            <h2 className="text-white font-black text-3xl md:text-5xl uppercase leading-tight">
              <span className="inline-flex items-center gap-3">
                <Instagram className="w-8 h-8 text-primary" /> ON THE GRID
              </span>
            </h2>
          </div>
          {data.username && (
            <a href={`https://instagram.com/${data.username}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-bold tracking-wider transition-colors">
              @{data.username} <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {media.map((m, i) => (
              <motion.a
                key={m.id}
                href={m.permalink}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="group relative aspect-square overflow-hidden bg-[#111115]"
                title={m.caption}
              >
                <img src={m.image} alt={m.caption || 'Instagram post'} width={300} height={300} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-[#0d0d0f]/0 group-hover:bg-[#0d0d0f]/60 transition-colors flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}